import type { HTMLAttributes, ReactNode } from "react";

export type ChipTone =
  | "neutral"
  | "accent"
  | "success"
  | "danger"
  | "info"
  // Warm/attention: a live, money-bearing state (an open restaurant invoice,
  // a pending buyer request). Derives from each app's own --warning token.
  | "warning"
  | "dark";

export type ChipSize = "sm" | "md";

export interface ChipProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: ChipTone;
  size?: ChipSize;
  leadingIcon?: ReactNode;
  /**
   * How a chip too wide for its column behaves.
   *
   * `nowrap` (default) keeps the historical single-line pill but now CLIPS
   * instead of spilling: the chip was `whitespace-nowrap` with no
   * `overflow-hidden`, so a long label rendered straight through its own
   * rounded border and read as a rendering bug. `wrap` lets the label run to a
   * second line and is the right choice for a chip carrying a short sentence.
   */
  overflow?: "nowrap" | "wrap";
  children: ReactNode;
}

const toneClasses: Record<ChipTone, string> = {
  neutral:
    "bg-[color:var(--background-muted)] text-[color:var(--text-secondary)] border-[color:var(--border-light)]",
  accent:
    "bg-[color-mix(in_oklab,var(--accent-500)_16%,white)] text-[color:var(--accent-600)] border-[color-mix(in_oklab,var(--accent-500)_30%,white)]",
  success:
    "bg-[color:var(--success-100)] text-[color:var(--success-500)] border-[color-mix(in_oklab,var(--success-500)_22%,white)]",
  danger:
    "bg-[color:var(--error-100)] text-[color:var(--error-500)] border-[color:var(--error-200)]",
  warning:
    "bg-[color-mix(in_oklab,var(--warning)_14%,var(--surface))] text-[color:var(--warning)] border-[color-mix(in_oklab,var(--warning)_32%,var(--surface))]",
  info: "bg-[color-mix(in_oklab,#0284c7_10%,white)] text-[#0284c7] border-[color-mix(in_oklab,#0284c7_20%,white)]",
  dark: "bg-[color:var(--color-hero-surface)] text-white border-[color:var(--color-hero-surface)]",
};

const sizeClasses: Record<ChipSize, string> = {
  sm: "text-chip px-2 py-0.5 gap-1",
  md: "text-meta px-2.5 py-1 gap-1.5",
};

/**
 * Generic inline status / tag chip used across the redesign (dirty markers,
 * "(New Soon)" pills, price deltas, etc.). Prefer `StatusBadge` for
 * server-driven statuses; `Chip` is the decorative / UI primitive.
 */
export function Chip({
  tone = "neutral",
  size = "sm",
  leadingIcon,
  overflow = "nowrap",
  children,
  className = "",
  ...rest
}: ChipProps) {
  return (
    <span
      {...rest}
      data-ui="chip"
      data-tone={tone}
      className={[
        "inline-flex max-w-full items-center rounded-full border font-medium leading-tight",
        overflow === "wrap"
          ? "text-start [overflow-wrap:anywhere]"
          : "min-w-0 overflow-hidden whitespace-nowrap",
        toneClasses[tone],
        sizeClasses[size],
        className,
      ].join(" ")}
    >
      {leadingIcon ? (
        <span aria-hidden="true" className="inline-flex shrink-0 items-center">
          {leadingIcon}
        </span>
      ) : null}
      {children}
    </span>
  );
}
